import { 
  collection, 
  doc, 
  addDoc, 
  setDoc,
  getDoc,
  getDocs,
  query,
  orderBy,
  serverTimestamp,
  Timestamp,
  deleteDoc
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { Message } from './geminiService';

export interface Conversation {
  id?: string;
  title: string;
  createdAt: Timestamp;
  updatedAt: Timestamp;
  firstMessage?: string;
  lastMessage?: string;
  contextId?: string;
  context?: any;
}

export interface SavedMessage extends Message {
  conversationId: string;
  userId: string;
}

class ConversationService {
  // Create a new conversation
  async createConversation(userId: string, title: string, firstMessage?: string): Promise<string> {
    try {
      const conversationsRef = collection(db, `wizardConversations/${userId}/conversations`);
      const conversationData: Omit<Conversation, 'id'> = {
        title,
        createdAt: serverTimestamp() as Timestamp,
        updatedAt: serverTimestamp() as Timestamp,
        ...(firstMessage && { firstMessage })
      };
      
      const docRef = await addDoc(conversationsRef, conversationData);
      return docRef.id;
    } catch (error) {
      console.error('Error creating conversation:', error);
      throw error;
    }
  }

  // Save a message to a conversation
  async saveMessage(userId: string, conversationId: string, message: Message): Promise<void> {
    try {
      const messagesRef = collection(
        db, 
        `wizardConversations/${userId}/conversations/${conversationId}/messages`
      );
      
      const messageData = {
        ...message,
        timestamp: serverTimestamp()
      };

      await addDoc(messagesRef, messageData);

      const conversationRef = doc(
        db, 
        `wizardConversations/${userId}/conversations/${conversationId}`
      );
      await setDoc(conversationRef, { 
        updatedAt: serverTimestamp(),
        lastMessage: message.content.substring(0, 100) + (message.content.length > 100 ? '...' : '')
      }, { merge: true });
    } catch (error) {
      console.error('❌ Error saving message:', error);
      throw error;
    }
  }

// Load a conversation with its context
async loadChat(userId: string, conversationId: string): Promise<Conversation | null> {
  try {
    const conversationRef = doc(
      db, 
      `wizardConversations/${userId}/conversations/${conversationId}`
    );
    const conversationSnap = await getDoc(conversationRef);
    
    if (!conversationSnap.exists()) {
      return null;
    }

    const conversation = {
      id: conversationSnap.id,
      ...conversationSnap.data()
    } as Conversation;

    // Load context if contextId exists
    if (conversation.contextId) {
      try {
        const contextRef = doc(
          db,
          `wizardConversations/${userId}/contexts/${conversation.contextId}`
        );
        const contextSnap = await getDoc(contextRef);
        
        if (contextSnap.exists()) {
          conversation.context = {
            id: contextSnap.id,
            ...contextSnap.data()
          };
          console.log('Context loaded for conversation:', conversation.contextId);
        } else {
          console.warn('Context not found for contextId:', conversation.contextId);
        }
      } catch (contextError) {
        console.error('Error loading context:', contextError);
        // Don't throw here - conversation can still work without context
      }
    }

    return conversation;
  } catch (error) {
    console.error('Error loading conversation:', error);
    throw error;
  }
}

  // Load all messages for a conversation
  async loadMessages(userId: string, conversationId: string): Promise<Message[]> {
    try {
      const messagesRef = collection(
        db, 
        `wizardConversations/${userId}/conversations/${conversationId}/messages`
      );
      const q = query(messagesRef, orderBy('timestamp', 'asc'));
      const querySnapshot = await getDocs(q);
      
      const messages = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as Message));

      return messages;
    } catch (error) {
      console.error('❌ Error loading messages:', error);
      throw error;
    }
  }

  // Load all conversations for a user
  async loadUserConversations(userId: string): Promise<Conversation[]> {
    try {
      const conversationsRef = collection(db, `wizardConversations/${userId}/conversations`);
      const q = query(conversationsRef, orderBy('updatedAt', 'desc'));
      const querySnapshot = await getDocs(q);
      
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as Conversation));
    } catch (error) {
      console.error('Error loading conversations:', error);
      throw error;
    }
  }

  // Delete a conversation and all its messages
  async deleteConversation(userId: string, conversationId: string): Promise<void> {
    try {
      // First, delete all messages in the conversation
      const messagesRef = collection(
        db, 
        `wizardConversations/${userId}/conversations/${conversationId}/messages`
      );
      const messagesSnapshot = await getDocs(messagesRef);
      
      // Delete each message
      const deletePromises = messagesSnapshot.docs.map(doc => 
        deleteDoc(doc.ref)
      );
      await Promise.all(deletePromises);
      
      // Then delete the conversation document itself
      const conversationRef = doc(
        db, 
        `wizardConversations/${userId}/conversations/${conversationId}`
      );
      await deleteDoc(conversationRef);
      
      console.log(`Conversation ${conversationId} and all its messages deleted successfully`);
    } catch (error) {
      console.error('Error deleting conversation:', error);
      throw error;
    }
  }
}

export const conversationService = new ConversationService();